// Global audit trail (F7): every proxied call across ALL clients — including
// authenticated-but-rejected ones — with filters, per-key stats and CSV
// export. Backed by the admin-only global surface: GET /admin/audit,
// /admin/audit/stats and /admin/audit/export.csv (spec §5).
//
// SECURITY: this page is the legitimate cross-tenant view that only the
// admin-gated service may construct — the client portal can never reach an
// unscoped query. Rows are sanitized server-side (reject_reason is an enum,
// statuses are ints; never raw upstream errors or key material). The CSV
// export goes through an AUTHENTICATED fetch into a blob download — the
// access token never appears in a URL.
//
// CHROME: NEXOVA AdminShell + nexus primitives (Card sections, DataTable,
// ToneBadge/StatusPill). The data hooks, filter params, and export flow are
// unchanged from the legacy screen.

import { useEffect, useState } from "react";
import { toast } from "sonner";

import { api } from "@/api/client";
import { errMessage } from "@/lib/errMessage";
import { getAccessToken } from "@/lib/auth";
import { API_BASE } from "@/lib/env";
import { AdminShell } from "@/components/nexus/AdminShell";
import {
  DataTable,
  StatusPill,
  ToneBadge,
  type Column,
} from "@/components/nexus";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

interface Client {
  id: string;
  name: string;
  email: string;
  enabled: boolean;
  created_at: number;
}

interface ClientList {
  clients: Client[];
}

interface AuditRecord {
  id: string;
  client_id: string;
  api_key_id: string;
  method: string;
  path: string;
  account_id: string;
  // What WE returned to the caller (0 = unknown/legacy row).
  response_status: number;
  // The upstream's answer (0 = nothing forwarded).
  tiktok_status: number;
  // "" = forwarded; else allowlist | rate_limit | key_disabled | client_disabled.
  reject_reason: string;
  latency_ms: number;
  // Unix seconds.
  timestamp: number;
}

interface AuditList {
  records: AuditRecord[];
}

interface EndpointCount {
  method: string;
  path: string;
  calls: number;
}

interface KeyStat {
  api_key_id: string;
  // Non-secret lookup prefix; "" when no client is selected or the key row
  // is gone (the audit trail outlives key deletion).
  prefix: string;
  enabled: boolean;
  calls: number;
  // Unix seconds.
  last_used: number;
  top_endpoints: EndpointCount[];
}

interface KeyStatsList {
  keys: KeyStat[];
}

const PAGE_SIZE = 50;

// Status filter options. The backend matches an EXACT response_status, so
// only a fully numeric selection is sent (spec §6); class options are kept
// as documented no-ops server-side.
const STATUS_OPTIONS = ["any", "2xx", "4xx", "429", "5xx"] as const;

// Converts an <input type="date"> value to unix seconds at request time
// (spec §6): from = start of day, to = end of day, local time. "" = unset.
function dateToUnix(value: string, endOfDay: boolean): number {
  if (!value) return 0;
  const ms = new Date(`${value}T${endOfDay ? "23:59:59" : "00:00:00"}`).getTime();
  if (!Number.isFinite(ms) || ms <= 0) return 0;
  return Math.floor(ms / 1000);
}

const selectClass =
  "h-9 rounded-lg border border-nx-border bg-nx-bg-2 px-2.5 text-sm text-nx-text outline-none focus-visible:border-nx-brand focus-visible:ring-3 focus-visible:ring-nx-brand/20";

export function AuditPage() {
  // Client selector (fed by GET /admin/clients). "" = all clients (global).
  const [clients, setClients] = useState<Client[]>([]);
  const [clientID, setClientID] = useState("");

  // Filters.
  const [apiKeyID, setApiKeyID] = useState("");
  const [pathPrefix, setPathPrefix] = useState("");
  const [status, setStatus] = useState("any");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [offset, setOffset] = useState(0);

  // Records.
  const [records, setRecords] = useState<AuditRecord[]>([]);
  const [recordsLoading, setRecordsLoading] = useState(true);
  const [recordsError, setRecordsError] = useState<string | null>(null);

  // Per-key stats (only fetched/rendered when a client is selected).
  const [stats, setStats] = useState<KeyStat[]>([]);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState<string | null>(null);

  const [exporting, setExporting] = useState(false);

  // The list/export filter set (spec §5 query params). Absent filters are
  // omitted entirely so the backend's zero-value defaults apply.
  function filterParams(): URLSearchParams {
    const qs = new URLSearchParams();
    if (clientID) qs.set("client_id", clientID);
    if (apiKeyID) qs.set("api_key_id", apiKeyID);
    if (pathPrefix) qs.set("path_prefix", pathPrefix);
    if (/^\d+$/.test(status)) qs.set("status", status);
    const from = dateToUnix(fromDate, false);
    if (from > 0) qs.set("from", String(from));
    const to = dateToUnix(toDate, true);
    if (to > 0) qs.set("to", String(to));
    return qs;
  }

  useEffect(() => {
    let cancelled = false;
    api
      .get<ClientList>("/admin/clients")
      .then((res) => {
        if (!cancelled) setClients(res.clients);
      })
      .catch((err) => {
        if (!cancelled) toast.error(errMessage(err));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setRecordsLoading(true);
    setRecordsError(null);
    const qs = filterParams();
    qs.set("limit", String(PAGE_SIZE));
    qs.set("offset", String(offset));
    api
      .get<AuditList>(`/admin/audit?${qs.toString()}`)
      .then((res) => {
        if (cancelled) return;
        setRecords(res.records);
        setRecordsLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setRecordsError(errMessage(err));
        setRecordsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientID, apiKeyID, pathPrefix, status, fromDate, toDate, offset]);

  useEffect(() => {
    let cancelled = false;
    setStatsLoading(true);
    setStatsError(null);
    // The backend's /admin/audit/stats accepts an empty client_id and returns
    // the cross-tenant key stats (prefix is "" cross-client), so the API-key
    // filter is usable in the "All clients" case too — never gate it on a
    // selected client.
    const statsURL = clientID
      ? `/admin/audit/stats?client_id=${encodeURIComponent(clientID)}`
      : "/admin/audit/stats";
    api
      .get<KeyStatsList>(statsURL)
      .then((res) => {
        if (cancelled) return;
        setStats(res.keys);
        setStatsLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setStatsError(errMessage(err));
        setStatsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientID]);

  // CSV export: authed fetch -> blob -> synthetic anchor download. NEVER a
  // raw link URL — that would put the bearer token in a URL (spec §6/§7).
  // Same filters as the list; the backend pages internally (caps at 50k),
  // so no limit/offset are sent.
  async function exportCSV() {
    setExporting(true);
    try {
      const qs = filterParams().toString();
      const token = getAccessToken();
      const res = await fetch(
        `${API_BASE}/admin/audit/export.csv${qs ? `?${qs}` : ""}`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          credentials: "include",
        },
      );
      if (!res.ok) {
        throw { status: res.status, message: "Export failed" };
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "tiktokproxy-audit.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setExporting(false);
    }
  }

  function clientName(id: string): string {
    return clients.find((c) => c.id === id)?.name ?? id;
  }

  function keyLabel(s: KeyStat): string {
    return s.prefix ? `${s.prefix}…` : s.api_key_id;
  }

  const recordColumns: Column<AuditRecord>[] = [
    {
      key: "time",
      header: "Time",
      cell: (r) => (
        <span className="text-sm text-nx-text-2 tabular-nums">
          {new Date(r.timestamp * 1000).toLocaleString()}
        </span>
      ),
    },
    {
      key: "client",
      header: "Client",
      cell: (r) => <span className="text-nx-text-2">{clientName(r.client_id)}</span>,
    },
    {
      key: "key",
      header: "Key",
      cell: (r) => (
        <span className="font-mono text-xs text-nx-text-2">
          {r.api_key_id || "—"}
        </span>
      ),
    },
    {
      key: "method",
      header: "Method",
      cell: (r) => <span className="text-nx-text-2">{r.method}</span>,
    },
    {
      key: "path",
      header: "Path",
      cell: (r) => (
        <code className="font-mono text-xs text-nx-text-2">{r.path}</code>
      ),
    },
    {
      key: "account",
      header: "Account",
      cell: (r) => (
        <span className="font-mono text-xs text-nx-text-2 tabular-nums">
          {r.account_id || "—"}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      cell: (r) => (
        <span className="flex items-center gap-2 tabular-nums text-nx-text-2">
          {r.response_status > 0 ? r.response_status : "—"}
          {r.reject_reason && (
            <ToneBadge tone="danger">rejected ({r.reject_reason})</ToneBadge>
          )}
        </span>
      ),
    },
    {
      key: "latency",
      header: "Latency",
      align: "right",
      cell: (r) => (
        <span className="font-mono text-xs text-nx-text-2 tabular-nums">
          {r.latency_ms}ms
        </span>
      ),
    },
  ];

  return (
    <AdminShell>
      <div className="grid gap-6">
        {/* Filter bar */}
        <Card>
          <CardHeader>
            <CardTitle>Audit log</CardTitle>
            <CardDescription>
              Every proxied call across all clients, including rejected ones
              (read-only, sanitized).
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap items-end gap-3">
            <label className="grid gap-1 text-sm">
              <span className="text-nx-text-2">Client</span>
              <select
                aria-label="Client"
                className={selectClass}
                value={clientID}
                onChange={(e) => {
                  setClientID(e.target.value);
                  // The key list belongs to the client; a tenant switch
                  // invalidates both the key filter and the page position.
                  setApiKeyID("");
                  setOffset(0);
                }}
              >
                <option value="">All clients</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="grid gap-1 text-sm">
              <span className="text-nx-text-2">API key</span>
              <select
                aria-label="API key"
                className={selectClass}
                value={apiKeyID}
                onChange={(e) => {
                  setApiKeyID(e.target.value);
                  setOffset(0);
                }}
              >
                <option value="">All keys</option>
                {stats.map((s) => (
                  <option key={s.api_key_id} value={s.api_key_id}>
                    {keyLabel(s)}
                  </option>
                ))}
              </select>
            </label>
            <label className="grid gap-1 text-sm">
              <span className="text-nx-text-2">Path prefix</span>
              <Input
                aria-label="Path prefix"
                className="w-56"
                placeholder="/open_api/v1.3/"
                value={pathPrefix}
                onChange={(e) => {
                  setPathPrefix(e.target.value);
                  setOffset(0);
                }}
              />
            </label>
            <label className="grid gap-1 text-sm">
              <span className="text-nx-text-2">Status</span>
              <select
                aria-label="Status"
                className={selectClass}
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value);
                  setOffset(0);
                }}
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>
            <label className="grid gap-1 text-sm">
              <span className="text-nx-text-2">From</span>
              <Input
                aria-label="From"
                type="date"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  setOffset(0);
                }}
              />
            </label>
            <label className="grid gap-1 text-sm">
              <span className="text-nx-text-2">To</span>
              <Input
                aria-label="To"
                type="date"
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                  setOffset(0);
                }}
              />
            </label>
            <Button onClick={exportCSV} disabled={exporting}>
              Export CSV
            </Button>
          </CardContent>
        </Card>

        {/* Per-key stats — only meaningful for one tenant, so only when a
            client is selected (the cross-client view has no prefix join). */}
        {clientID && (
          <div className="grid gap-4 md:grid-cols-2">
            {statsError && (
              <Alert variant="destructive" className="md:col-span-2">
                <AlertTitle>Could not load key stats</AlertTitle>
                <AlertDescription>{statsError}</AlertDescription>
              </Alert>
            )}
            {statsLoading ? (
              <>
                <Skeleton className="h-32 w-full" />
                <Skeleton className="h-32 w-full" />
              </>
            ) : (
              stats.map((s) => (
                <Card key={s.api_key_id}>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <code className="font-mono text-nx-text">
                        {keyLabel(s)}
                      </code>
                      <StatusPill tone={s.enabled ? "credit" : "danger"}>
                        {s.enabled ? "enabled" : "disabled"}
                      </StatusPill>
                    </CardTitle>
                    <CardDescription>
                      {s.calls} calls · last used{" "}
                      {new Date(s.last_used * 1000).toLocaleString()}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {s.top_endpoints.length === 0 ? (
                      <p className="text-sm text-nx-text-3">
                        No calls in the last 30 days.
                      </p>
                    ) : (
                      <ul className="grid gap-1 text-sm">
                        {s.top_endpoints.map((e) => (
                          <li key={`${e.method} ${e.path}`}>
                            <code className="font-mono text-xs text-nx-text-2">
                              {e.method} {e.path} — {e.calls}
                            </code>
                          </li>
                        ))}
                      </ul>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Records table */}
        <Card>
          <CardContent className="grid gap-3">
            {recordsError && (
              <Alert variant="destructive">
                <AlertTitle>Could not load audit records</AlertTitle>
                <AlertDescription>{recordsError}</AlertDescription>
              </Alert>
            )}
            {recordsLoading ? (
              <div className="grid gap-2">
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
              </div>
            ) : (
              <DataTable
                columns={recordColumns}
                rows={records}
                rowKey={(r) => r.id}
                empty="No audit records for this page."
              />
            )}
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={offset === 0 || recordsLoading}
                onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
              >
                Prev
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={records.length < PAGE_SIZE || recordsLoading}
                onClick={() => setOffset((o) => o + PAGE_SIZE)}
              >
                Next
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminShell>
  );
}
